// src/platform/OrgSeatsPanel.jsx
//
// Per-tier seat management for an org. Shows:
//   1. Active pool — aggregated purchased vs assigned per tier
//   2. Grant Seats button — opens modal for new purchase row
//   3. Purchase history — all seat rows (active, suspended, expired)
//
// Edits to historical rows happen inline via a small modal.
import { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import {
  LICENSE_TIER_OPTIONS,
  LICENSE_TIER_LABEL,
  DEFAULT_LICENSE_TIER,
} from "../constants/licenseTiers";

export default function OrgSeatsPanel({ orgId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGrant, setShowGrant] = useState(false);
  const [editRow, setEditRow] = useState(null);

  async function load() {
    if (!data) setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`/api/v1/platform/orgs/${orgId}/seats`);
      if (!res.ok) throw new Error("Failed to load seats");
      setData(await res.json());
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [orgId]);

  if (loading) {
    return (
      <div className="flex justify-center pt-6">
        <Loader2 className="animate-spin text-gray-400" size={20} />
      </div>
    );
  }

  if (error) return <div className="text-xs text-red-400">{error}</div>;
  if (!data) return null;

  const { tier_summary = [], seat_rows = [] } = data;

  return (
    <div className="space-y-4">
      {/* ACTIVE POOL */}
      <div className="rounded border border-bg-border bg-bg p-3 space-y-3">
        <div className="text-xs text-gray-300 font-semibold">Active Pool</div>

        {tier_summary.length === 0 ? (
          <div className="text-xs text-fg-secondary italic">
            No active seat purchases.
          </div>
        ) : (
          tier_summary.map(t => {
            const used = Number(t.seats_assigned) || 0;
            const total = Number(t.seats_purchased_total) || 0;
            const label = LICENSE_TIER_LABEL[t.tier_code] ?? t.display_name;

            if (t.seats_unlimited) {
              return (
                <div key={t.tier_code} className="flex justify-between items-center text-xs">
                  <span className="text-gray-400">{label}</span>
                  <span className="text-gray-100">
                    {used} <span className="text-fg-secondary font-normal">assigned · unlimited</span>
                  </span>
                </div>
              );
            }

            const pct = total > 0 ? (used / total) * 100 : 0;
            const available = Math.max(0, total - used);

            return (
              <div key={t.tier_code}>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-400">{label}</span>
                  <span className="text-gray-100">
                    {used} / {total}{" "}
                    <span className="text-fg-secondary font-normal">
                      ({available} available)
                    </span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded bg-bg-soft">
                  <div
                    className={`h-1.5 rounded transition-[width] duration-200 ${pct >= 100 ? "bg-red-500"
                      : pct >= 80 ? "bg-yellow-500"
                        : "bg-status-ok"
                      }`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* GRANT BUTTON */}
      <div>
        <button
          onClick={() => setShowGrant(true)}
          className="
            flex items-center gap-2 px-3 py-1.5 rounded
            bg-accent-action/20 border border-accent-action/60
            text-accent-action hover:bg-accent-action/30 transition text-xs
          "
        >
          <Plus size={14} />
          Grant Seats
        </button>
      </div>

      {/* PURCHASE HISTORY */}
      <div>
        <div className="text-xs text-gray-300 font-semibold mb-2">
          Purchase History
        </div>

        {seat_rows.length === 0 ? (
          <div className="text-xs text-fg-secondary italic">
            No purchase history.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-gray-400">
              <tr>
                <th className="text-left py-1">Tier</th>
                <th className="text-left py-1">Seats</th>
                <th className="text-left py-1">Term</th>
                <th className="text-left py-1">Contract</th>
                <th className="text-left py-1">Status</th>
                <th className="text-left py-1"></th>
              </tr>
            </thead>
            <tbody>
              {seat_rows.map(r => {
                const label = LICENSE_TIER_LABEL[r.tier_code] ?? r.tier_display_name;
                const termStart = new Date(r.term_start).toLocaleDateString();
                const termEnd = r.term_end
                  ? new Date(r.term_end).toLocaleDateString()
                  : "—";
                const statusColor = {
                  active: "border-green-500/40 text-green-400",
                  suspended: "border-yellow-500/40 text-yellow-400",
                  expired: "border-gray-500/40 text-gray-400",
                }[r.status] || "border-gray-500/40 text-gray-400";

                return (
                  <tr key={r.id} className="border-t border-bg-border">
                    <td className="py-1.5">{label}</td>
                    <td className="py-1.5">{r.seats_purchased}</td>
                    <td className="py-1.5 text-gray-400">
                      {termStart} – {termEnd}
                    </td>
                    <td className="py-1.5 text-gray-400">
                      {r.contract_ref || "—"}
                    </td>
                    <td className="py-1.5">
                      <span className={`px-2 py-0.5 rounded border ${statusColor}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-1.5 text-right">
                      <button
                        onClick={() => setEditRow(r)}
                        className="text-gray-400 hover:text-gray-200"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* GRANT MODAL */}
      {showGrant && (
        <GrantSeatsModal
          orgId={orgId}
          onClose={() => setShowGrant(false)}
          onSuccess={async () => {
            setShowGrant(false);
            await load();
          }}
        />
      )}

      {/* EDIT MODAL */}
      {editRow && (
        <EditSeatRowModal
          orgId={orgId}
          row={editRow}
          onClose={() => setEditRow(null)}
          onSuccess={async () => {
            setEditRow(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// GrantSeatsModal — create a new org_license_seats row
// ──────────────────────────────────────────────────────────
function GrantSeatsModal({ orgId, onClose, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);
  const [tier, setTier] = useState(DEFAULT_LICENSE_TIER);
  const [seats, setSeats] = useState(10);
  const [termStart, setTermStart] = useState(today);
  const [termEnd, setTermEnd] = useState("");
  const [contractRef, setContractRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`/api/v1/platform/orgs/${orgId}/seats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier_code: tier,
          seats_purchased: Number(seats),
          term_start: termStart,
          term_end: termEnd || null,
          contract_ref: contractRef || null,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.error || "Grant failed");
      }

      await onSuccess();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-[420px] bg-bg-soft border border-bg-border rounded p-4 space-y-4">
        <h2 className="text-sm font-semibold">Grant Seats</h2>

        <div>
          <label className="block text-xs mb-1 text-gray-400">Tier</label>
          <select
            value={tier}
            onChange={e => setTier(e.target.value)}
            className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
          >
            {LICENSE_TIER_OPTIONS.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs mb-1 text-gray-400">Seats</label>
          <input
            type="number"
            min="1"
            value={seats}
            onChange={e => setSeats(e.target.value)}
            className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs mb-1 text-gray-400">Term Start</label>
            <input
              type="date"
              value={termStart}
              onChange={e => setTermStart(e.target.value)}
              className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
            />
          </div>
          <div>
            <label className="block text-xs mb-1 text-gray-400">
              Term End <span className="text-fg-secondary">(optional)</span>
            </label>
            <input
              type="date"
              value={termEnd}
              onChange={e => setTermEnd(e.target.value)}
              className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs mb-1 text-gray-400">
            Contract Ref <span className="text-fg-secondary">(optional)</span>
          </label>
          <input
            value={contractRef}
            onChange={e => setContractRef(e.target.value)}
            placeholder="ACME-2026-001"
            className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
          />
        </div>

        {error && <div className="text-xs text-red-400">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1 rounded border border-bg-border text-gray-400 text-xs"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="
              px-3 py-1 rounded text-xs
              bg-accent-action/20 border border-accent-action/60
              text-accent-action
            "
          >
            {submitting ? "Granting…" : "Grant"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// EditSeatRowModal — modify existing row (with backend guards)
// ──────────────────────────────────────────────────────────
function EditSeatRowModal({ orgId, row, onClose, onSuccess }) {
  const [seats, setSeats] = useState(row.seats_purchased);
  const [termEnd, setTermEnd] = useState(
    row.term_end ? row.term_end.slice(0, 10) : ""
  );
  const [contractRef, setContractRef] = useState(row.contract_ref || "");
  const [status, setStatus] = useState(row.status);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithAuth(
        `/api/v1/platform/orgs/${orgId}/seats/${row.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            seats_purchased: Number(seats),
            term_end: termEnd || null,
            contract_ref: contractRef,
            status,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.error || "Update failed");
      }

      await onSuccess();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-[420px] bg-bg-soft border border-bg-border rounded p-4 space-y-4">
        <h2 className="text-sm font-semibold">Edit Seat Row</h2>

        <div className="text-xs text-fg-secondary italic">
          {row.tier_code} · purchased {new Date(row.term_start).toLocaleDateString()}
        </div>

        <div>
          <label className="block text-xs mb-1 text-gray-400">Seats Purchased</label>
          <input
            type="number"
            min="1"
            value={seats}
            onChange={e => setSeats(e.target.value)}
            className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
          />
        </div>

        <div>
          <label className="block text-xs mb-1 text-gray-400">Term End</label>
          <input
            type="date"
            value={termEnd}
            onChange={e => setTermEnd(e.target.value)}
            className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
          />
        </div>

        <div>
          <label className="block text-xs mb-1 text-gray-400">Contract Ref</label>
          <input
            value={contractRef}
            onChange={e => setContractRef(e.target.value)}
            className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
          />
        </div>

        <div>
          <label className="block text-xs mb-1 text-gray-400">Status</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
          >
            <option value="active">active</option>
            <option value="suspended">suspended</option>
            <option value="expired">expired</option>
          </select>
        </div>

        {error && <div className="text-xs text-red-400">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1 rounded border border-bg-border text-gray-400 text-xs"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="
              px-3 py-1 rounded text-xs
              bg-accent-action/20 border border-accent-action/60
              text-accent-action
            "
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}